import {
  BaseIdentifiers,
  ProgramIdentifier,
  PurchaseIdentifier,
  TradingSystemIdentifier,
  UserIdentifier,
} from '../common/identifiers';
import { Contract } from '../contract';
import { PartnerType } from '../partner';
import { AssetClass } from '../program/program';
import { TradingSystemCredentials } from '../trading-system';
import { User } from '../user/user';

export const MAX_CREATION_AMOUNT = 5;

export type AccountWithdrawalItem = {
  chargedAmount: number;
  chargedCredits: number;
  payoutId: string;
};

export type AccountAdmin = Account & {
  user: User;
  balance: string;
  credits: number;
  programName: string;
  contract: Contract & BaseIdentifiers;
};

export type Account = BaseIdentifiers &
  UserIdentifier &
  PurchaseIdentifier &
  ProgramIdentifier &
  TradingSystemIdentifier &
  TradingSystemCredentials & {
    status: AccountStatus;
    type: AccountType;
    state: AccountState;
    expireDate: Date;
    boosters: BoosterItem[];
    level: number;
    previousProgramId?: string;
    disableReason?: string | DisabledReason;
    notes?: string;
    terminationReason?: TerminationReason;
    creationRetries?: number;
    config?: AccountConfig;
    additionalPayment?: number;
    assetClass: AssetClass;
    metadata?: AccountMetadata;
  };

export interface BoosterItem {
  type: PartnerType;
  id: string;
}

export interface AccountConfig {
  hasSeen: boolean;
  fundedLevel?: number;
  terminationReasonSeen?: boolean;
  subscriptionStatus?: 'canceled';
}

export interface AccountMetadata {
  correlationHash?: string;
  initialFunds?: number;
  initialProfitCredits?: number;
  initialRefundCredits?: number;
}

export enum AccountType {
  Evaluation = 'evaluation',
  Competition = 'competition',
  Funded = 'funded',
  Demo = 'demo',
}

export enum AccountStatus {
  Active = 'active',
  Disabled = 'disabled',
  PendingCreation = 'pending-creation',
  AwaitingAsyncCreation = 'awaiting-async-creation',
  PendingAsyncCreation = 'pending-async-creation',
  AccountCreationFailure = 'account-creation-failure',
  AccountCreationDidntStart = 'account-creation-didnt-start',
  WaitingForRisk = 'waiting-for-risk',
  WaitingForWithdraw = 'waiting-for-withdraw',
  ReachedTarget = 'reached-target',
}

export enum AccountStateKey {
  Terminated = 'terminated',
  DailyPause = 'dailyPause',
  DailyLoss = 'dailyLoss',
  Target = 'target',
  Expired = 'expired',
}

export interface AccountState {
  [AccountStateKey.Terminated]: boolean;
  [AccountStateKey.DailyPause]: boolean;
  [AccountStateKey.DailyLoss]: boolean;
  [AccountStateKey.Target]: boolean;
  [AccountStateKey.Expired]: boolean;
}

export enum TerminationReason {
  Cancelled = 'Cancelled',
  Refunded = 'Refunded',
  Withdraw = 'Withdraw',
  OverNight = 'OverNight',
  Weekend = 'Weekend',
}

export enum TradingSystemAccountState {
  Active = '',
  DailyPause = 'DP',
  DailyLoss = 'DL',
  Passed = 'P',
  Terminated = 'T',
  OverNightEnforcement = 'OEN',
  WeekendEnforcement = 'WEN',
  Withdraw = 'W',
  EndContest = 'EC',
  Cancelled = 'C',
  Expired = 'E',
  Refunded = 'R',
  WaitingForRisk = 'WR',
}

export const accountTradingSystemStateMap = {
  [AccountStateKey.DailyLoss]: TradingSystemAccountState.DailyLoss,
  [AccountStateKey.DailyPause]: TradingSystemAccountState.DailyPause,
  [AccountStateKey.Expired]: TradingSystemAccountState.Expired,
  [AccountStateKey.Target]: TradingSystemAccountState.Passed,
  [AccountStateKey.Terminated]: TradingSystemAccountState.Terminated,
};

export type TsAccountAutoCompleteKeys = 'tsAccount' | 'tsAccountId';

export type AccountAdminSearchableKeys =
  | TsAccountAutoCompleteKeys
  | 'purchaseId'
  | 'userId';

export const DISABLED_REASONS = {
  NameMismatch: 'Name mismatch',
  EmailMismatch: 'Email mismatch',
  CardHolderNameMismatch: 'Card holder name mismatch',
  BINCountryMismatch: 'BIN country mismatch',
  IPCountryMismatch: 'IP country mismatch',
  MultipleFailedAttemptsToDepositCheck:
    'Multiple failed attempts to deposit check',
  MultipleCardsCheck: 'Multiple cards check',
  OneSidedBetting: 'One sided betting',
  BreachedOneSidedBetting: 'Breached one sided betting',
  BulkTrading: 'Bulk trading',
  BreachedBulkTrading: 'Breached bulk trading',
  BootcampViolation: 'Bootcamp violation',
  BootcampStrategy: 'Bootcamp strategy',
  MaximumAccountsNotification: 'Maximum accounts notification',
  Interview: 'Interview',
  Group: 'Group',
  Chargeback: 'Chargeback',
  Review: 'Review',
  ProfitTargetIssue: 'Profit target issue',
  Fraud: 'Fraud',
  Other: 'Other',
};

export const DisabledReasons = Object.values(DISABLED_REASONS);

export type DisabledReason = keyof typeof DISABLED_REASONS;
